<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-chart-line</v-icon>
      Abandoned at Step — Monthly Trend
    </v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else-if="series.length === 0" class="text-center py-8 text-medium-emphasis">
        No data available
      </div>
      <div v-else>
        <apexchart
          type="area"
          height="400"
          :options="chartOptions"
          :series="series"
        />

        <v-divider class="my-4"></v-divider>

        <div class="d-flex align-center mb-2">
          <v-icon size="small" class="mr-2">mdi-table</v-icon>
          <span class="text-subtitle-2 font-weight-bold">Monthly Breakdown</span>
          <v-spacer></v-spacer>
          <span class="text-caption text-medium-emphasis">
            Count (% of month)
          </span>
        </div>

        <div class="table-scroll">
          <v-table density="compact" class="breakdown-table">
            <thead>
              <tr>
                <th class="text-left">Month</th>
                <th v-for="(s, i) in series" :key="s.name" class="text-right">
                  <div class="d-flex align-center justify-end">
                    <span
                      class="step-dot mr-2"
                      :style="{ backgroundColor: stepColor(i) }"
                    ></span>
                    {{ s.name }}
                  </div>
                </th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableRows" :key="row.month">
                <td class="font-weight-medium text-no-wrap">{{ row.month }}</td>
                <td v-for="(cell, i) in row.cells" :key="i" class="text-right">
                  <span class="font-weight-bold">{{ cell }}</span>
                  <span class="text-caption text-medium-emphasis ml-1">
                    {{ row.total > 0 ? ((cell / row.total) * 100).toFixed(1) : "0.0" }}%
                  </span>
                </td>
                <td class="text-right font-weight-bold">{{ row.total }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td class="font-weight-bold">Total</td>
                <td v-for="(total, i) in stepTotals" :key="i" class="text-right">
                  <span class="font-weight-bold">{{ total }}</span>
                  <span class="text-caption text-medium-emphasis ml-1">
                    {{ grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : "0.0" }}%
                  </span>
                </td>
                <td class="text-right font-weight-bold">{{ grandTotal }}</td>
              </tr>
            </tfoot>
          </v-table>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import { comebackService } from "../services";

export default {
  name: "AbandonedStepsTimelineChart",
  components: { apexchart: VueApexCharts },
  props: {
    startDate: { type: [String, Date], default: null },
    endDate: { type: [String, Date], default: null },
  },
  data() {
    return {
      loading: true,
      series: [],
      chartOptions: {
        chart: {
          type: "area",
          stacked: true,
          toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
        },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.5, opacityTo: 0.1 } },
        xaxis: {
          categories: [],
          title: { text: "Month" },
        },
        yaxis: {
          title: { text: "Abandonments" },
          decimalsInFloat: 0,
          labels: { formatter: (val) => Math.round(val) },
        },
        colors: [
          "#1976D2", "#E91E63", "#4CAF50", "#FF9800", "#9C27B0",
          "#00BCD4", "#FF5722", "#3F51B5", "#8BC34A", "#F44336",
          "#009688", "#FFC107", "#673AB7", "#03A9F4", "#CDDC39",
        ],
        legend: { position: "top" },
        dataLabels: { enabled: false },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (val, { seriesIndex, dataPointIndex, w }) {
              const allVals = w.globals.series.map(s => s[dataPointIndex] || 0);
              const total = allVals.reduce((a, b) => a + b, 0);
              const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
              return `${val} (${pct}%)`;
            },
          },
        },
      },
    };
  },
  computed: {
    // Single key for both bounds so moving the range triggers one reload.
    rangeKey() {
      return `${this.toYmd(this.startDate)}|${this.toYmd(this.endDate)}`;
    },
    months() {
      return this.chartOptions.xaxis.categories || [];
    },
    // One row per month, mirroring the chart: the cells line up with the
    // series (steps) in the same order as the legend.
    tableRows() {
      return this.months.map((month, monthIndex) => {
        const cells = this.series.map((s) => s.data[monthIndex] || 0);
        return {
          month,
          cells,
          total: cells.reduce((a, b) => a + b, 0),
        };
      });
    },
    stepTotals() {
      return this.series.map((s) =>
        s.data.reduce((a, b) => a + (b || 0), 0)
      );
    },
    grandTotal() {
      return this.stepTotals.reduce((a, b) => a + b, 0);
    },
  },
  watch: {
    rangeKey() { this.loadData(); },
  },
  methods: {
    // Same palette the chart uses, so a column header dot matches its area.
    stepColor(index) {
      const colors = this.chartOptions.colors;
      return colors[index % colors.length];
    },
    toYmd(d) {
      if (!d) return undefined;
      const dt = new Date(d);
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const day = String(dt.getDate()).padStart(2, "0");
      return `${dt.getFullYear()}-${m}-${day}`;
    },
    async loadData() {
      this.loading = true;
      try {
        const response = await comebackService.getAbandonedStepsTimeline({
          startDate: this.toYmd(this.startDate),
          endDate: this.toYmd(this.endDate),
        });
        if (response?.success && response.data) {
          const { months, series } = response.data;
          this.series = series;
          this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
              ...this.chartOptions.xaxis,
              categories: months,
            },
          };
        } else {
          this.series = [];
        }
      } catch (e) {
        console.error("Failed to load abandoned steps timeline", e);
        this.series = [];
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
/* Keep a wide breakdown scrollable inside the card instead of stretching it. */
.table-scroll {
  overflow-x: auto;
}

.breakdown-table :deep(th) {
  white-space: nowrap;
}

.step-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.totals-row td {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
